import { Injectable, Injector, ErrorHandler } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Opt a request out of the global error snackbar when the caller renders its
 * own feedback (e.g. inline validation messages in a dialog).
 */
export const SKIP_GLOBAL_ERROR_SNACKBAR = new HttpContextToken<boolean>(() => false);

/**
 * Narrower than SKIP_GLOBAL_ERROR_SNACKBAR: only these statuses are left to the caller, every
 * other failure still gets the snackbar.
 */
export const SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS = new HttpContextToken<readonly number[]>(
  () => [],
);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private injector: Injector,
    private _snackBar: MatSnackBar,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          // The application-wide handler reports to Application Insights and then rethrows.
          // Left unguarded that rethrow escapes this callback, so the snackbar below is never
          // reached and an API message explaining the failure never gets to the user.
          try {
            const appErrorHandler = this.injector.get(ErrorHandler);
            appErrorHandler.handleError(error);
          } catch {
            // Reporting only; the HTTP error itself is rethrown at the end of this callback.
          }

          if (
            !request.context.get(SKIP_GLOBAL_ERROR_SNACKBAR) &&
            !request.context.get(SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS).includes(error.status)
          ) {
            // Guard against empty/object bodies (e.g. a 500 with no body, or a
            // 422 whose `detail` is an array) so we never throw or show "[object Object]".
            const message = error.error?.message || error.error?.detail;
            if (typeof message === 'string' && message.length > 0) {
              this.showSnackBarError(message);
            }
          }
        }
        return throwError(() => error);
      }),
    );
  }

  showSnackBarError(message: string) {
    this._snackBar.open(message, '', {
      horizontalPosition: 'right',
      verticalPosition: 'top',
      duration: 3000,
    });
  }
}
